/**
 * Service Worker Utilities
 * Handles Service Worker registration, updates, and development mode detection
 */

// Check if we're in development mode
export const isDevelopment = () => {
  return process.env.NODE_ENV === 'development' || 
         window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.includes('localhost');
};

// Check if Service Worker is supported
export const isServiceWorkerSupported = () => {
  return 'serviceWorker' in navigator;
};

// Get Service Worker registration
export const getServiceWorkerRegistration = async () => {
  if (!isServiceWorkerSupported()) {
    return null;
  }
  
  try {
    return await navigator.serviceWorker.getRegistration();
  } catch (error) {
    // Error getting Service Worker registration
    return null;
  }
};

// Check if Service Worker is ready
export const isServiceWorkerReady = async () => {
  if (!isServiceWorkerSupported()) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    return !!registration;
  } catch (error) {
    // Service Worker not ready
    return false;
  }
};

// Handle Service Worker updates
export const handleServiceWorkerUpdate = (registration) => {
  if (!registration) return;
  
  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing;
    if (newWorker) {
      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New Service Worker available
          
          if (isDevelopment()) {
            // In development, skip waiting and reload immediately
            newWorker.postMessage({ action: 'skipWaiting' });
            setTimeout(() => {
              window.location.reload();
            }, 100);
          } else {
            // In production, notify user about update
            // New version available. Please refresh the page.
            // You could show a toast notification here
          }
        }
      });
    }
  });
};

// Register Service Worker with enhanced error handling
export const registerServiceWorker = async (swPath = '/sw.js') => {
  if (!isServiceWorkerSupported()) {
    // Service Worker not supported
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register(swPath);
    // Service Worker registered successfully
    
    // Handle updates
    handleServiceWorkerUpdate(registration);
    
    return registration;
  } catch (error) {
    // Service Worker registration failed
    return null;
  }
};

// Unregister Service Worker (useful for development)
export const unregisterServiceWorker = async () => {
  if (!isServiceWorkerSupported()) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.unregister();
      // Service Worker unregistered
      return true;
    }
    return false;
  } catch (error) {
    // Error unregistering Service Worker
    return false;
  }
};

// Clear Service Worker cache
export const clearServiceWorkerCache = async () => {
  if (!isServiceWorkerSupported()) {
    return false;
  }
  
  try {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );
    // Service Worker cache cleared
    return true;
  } catch (error) {
    // Error clearing Service Worker cache
    return false;
  }
};

// Check for Service Worker update issues
export const checkServiceWorkerHealth = async () => {
  if (!isServiceWorkerSupported()) {
    return { healthy: true, message: 'Service Worker not supported' };
  }
  
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return { healthy: false, message: 'No Service Worker registered' };
    }
    
    const isReady = await isServiceWorkerReady();
    if (!isReady) {
      return { healthy: false, message: 'Service Worker not ready' };
    }
    
    return { healthy: true, message: 'Service Worker is healthy' };
  } catch (error) {
    return { healthy: false, message: `Service Worker error: ${error.message}` };
  }
};

// Development helper to reset Service Worker state
export const resetServiceWorkerForDevelopment = async () => {
  if (!isDevelopment()) {
    // resetServiceWorkerForDevelopment should only be used in development
    return;
  }
  
  try {
    // Unregister current Service Worker
    await unregisterServiceWorker();
    
    // Clear cache
    await clearServiceWorkerCache();
    
    // Reload page
    window.location.reload();
  } catch (error) {
    // Error resetting Service Worker
  }
};

// Listen for Service Worker messages
export const setupServiceWorkerMessageListener = (callback) => {
  if (!isServiceWorkerSupported()) {
    return;
  }
  
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (callback) {
      callback(event.data);
    }
  });
};

// Send message to Service Worker
export const sendMessageToServiceWorker = async (message) => {
  if (!isServiceWorkerSupported() || !navigator.serviceWorker.controller) {
    return false;
  }
  
  try {
    navigator.serviceWorker.controller.postMessage(message);
    return true;
  } catch (error) {
    // Error sending message to Service Worker
    return false;
  }
};

export default {
  isDevelopment,
  isServiceWorkerSupported,
  getServiceWorkerRegistration,
  isServiceWorkerReady,
  handleServiceWorkerUpdate,
  registerServiceWorker,
  unregisterServiceWorker,
  clearServiceWorkerCache,
  checkServiceWorkerHealth,
  resetServiceWorkerForDevelopment,
  setupServiceWorkerMessageListener,
  sendMessageToServiceWorker
};
